import { z } from "zod";
import {
  booleanString,
  patientGenderSchema,
  professionTypeSchema,
  UiLanguageSchema,
} from "./utils";
import { passwordSchema } from "./password";

/**
 * The optional half of a staff member's professional identity. Every one of
 * them is nullable on the row and therefore on the payload, and OPTIONAL on
 * the way in (see the `.partial()` masks below): a create form that has to
 * send `professionalTitle: null` to leave a title blank is a form that will
 * send the wrong thing.
 *
 * The two register numbers are checked for shape and nothing more. RPPS is 11
 * digits and ADELI is 9; both carry no checksum this code can verify, so the
 * length is the whole of what the API can honestly claim to know. Neither is
 * unique: a mistyped number with a unique index locks out the person who
 * actually holds it.
 */
const optionalProfessionalFields = {
  professionalTitle: z.string().trim().min(1).max(32).nullable(),
  specialty: z.string().trim().min(1).max(120).nullable(),
  subSpecialty: z.string().trim().min(1).max(120).nullable(),
  licenseNumber: z.string().trim().min(1).max(64).nullable(),
  rppsNumber: z
    .string()
    .trim()
    .regex(/^\d{11}$/, "An RPPS number is 11 digits")
    .nullable(),
  adeliNumber: z
    .string()
    .trim()
    .regex(/^\d{9}$/, "An ADELI number is 9 digits")
    .nullable(),
} as const;

/** The keys above, as a `.partial()` / `.omit()` mask. */
const optionalProfessionalMask = {
  professionalTitle: true,
  specialty: true,
  subSpecialty: true,
  licenseNumber: true,
  rppsNumber: true,
  adeliNumber: true,
} as const;

export const userResponseSchema = z.object({
  id: z.string(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  gender: patientGenderSchema,
  activated: z.boolean(),
  // coerce, not z.date(): the contract must parse on BOTH ends, and a browser
  // client parsing a JSON response only ever sees an ISO string here.
  activatedAt: z.coerce.date().nullable(),
  phone: z
    .string()
    .regex(/^\+?[1-9]\d{1,14}$/)
    .nullable(),
  birthdate: z.coerce.date().nullable(),
  isArchived: z.boolean(),
  isEnabled: z.boolean(),
  /**
   * Every capability except EXPORT_DATA (which is grant-only; see
   * capabilities.ts). This is the escape valve for the person whose rights
   * outgrow their profession: the profession row stays honest and the flag
   * says "and this one administers the deployment".
   */
  isAdmin: z.boolean(),
  /** Server-owned. Set by the seed, never writable through the API. */
  isSuperAdmin: z.boolean(),
  /**
   * The clinician's own UI language, saved on the account so it follows them to
   * any browser rather than living in one machine's localStorage.
   *
   * NULL means "never chosen", which is deliberately distinct from having
   * chosen the default: the client applies its own default rule (French, since
   * a deployment serves one facility) and only overrides it when there is a
   * real preference to honour.
   *
   * Any two-letter ISO 639-1 code, not the dashboard's own two-language list:
   * the API does not know that list and must not need a redeploy when it grows.
   * A code no client ships a catalogue for falls back on the client, which is
   * what it already does for an absent one.
   */
  preferredLanguage: UiLanguageSchema.nullable(),

  /**
   * What this person is, professionally, and the key into the baseline
   * capability matrix (see `professionTypeSchema`). Clients still must not
   * branch on it for gating: the server sends conclusions (`capabilities` on
   * /auth/me, `permissions` on scoped resources) precisely so nobody
   * re-evaluates the matrix client-side.
   */
  professionType: professionTypeSchema,
  ...optionalProfessionalFields,

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /** Null on an active account, and null on rows archived before this column
   * existed: the instant was not recorded and cannot be recovered. */
  archivedAt: z.coerce.date().nullable(),

  /**
   * Whether the failed-login lock is on. DERIVED from the lock instant rather
   * than stored beside it, so there is one fact and not two that can disagree.
   * The lock has no expiry: only completing a password reset clears it, which
   * is what this flag is for, telling an admin why a colleague cannot log in.
   *
   * Why the account is locked is deliberately NOT here. Failed logins are the
   * only producer today, and the reason column is written for a forensic query
   * rather than for a screen.
   */
  accountLocked: z.boolean(),
});

export type UserResponse = z.infer<typeof userResponseSchema>;

/**
 * Fields written by the server and never accepted from a client: audit stamps
 * and a derived flag. Split out because both write schemas below omit exactly
 * this set, and a field added to the response has to be refused by both or it
 * silently becomes writable.
 */
const serverOwnedUserFields = {
  createdAt: true,
  updatedAt: true,
  archivedAt: true,
  accountLocked: true,
} as const;

export const listUsersRequestSchema = {
  querystring: z.object({
    page: z.coerce.number().int().positive("Page must be at least 1"),
    pageSize: z.coerce.number().int().positive().optional(),
    isEnabled: booleanString.optional(),
    isArchived: booleanString.optional(),
  }),
  response: {
    200: z.object({
      users: z.array(userResponseSchema),
      totalPages: z.number(),
      currentPage: z.number(),
    }),
  },
};
export type ListUsersQuery = z.infer<typeof listUsersRequestSchema.querystring>;

export const createUserRequestSchema = {
  body: userResponseSchema
    .omit({
      id: true,
      isArchived: true,
      isEnabled: true,
      // Server-controlled: superadmin is not something a create form may grant.
      isSuperAdmin: true,
      // Nobody chooses someone else's UI language: it is set by its owner from
      // their own account page, and a new account has no preference until then.
      preferredLanguage: true,
      ...serverOwnedUserFields,
    })
    // `professionType` stays required: it is what the account IS, and an
    // account with no answer to that is a row nobody will ever go back and fix.
    .partial(optionalProfessionalMask)
    // Optional-in, false by default. Safe to accept from a create form only
    // because POST /users itself requires MANAGE_USERS: the people who can
    // reach this field are exactly the people allowed to mint admins.
    .extend({ isAdmin: z.boolean().default(false) }),
  response: { 201: userResponseSchema },
};
export type CreateUserBody = z.infer<typeof createUserRequestSchema.body>;

/**
 * Changing your own password re-authenticates you.
 *
 * Holding a live session must not be enough to take an account over: an
 * unlocked workstation or a stolen refresh cookie would otherwise let someone
 * set a new password, which also drops every session and locks the owner out.
 * The reset-token shape below deliberately has no such field — someone who
 * forgot their password cannot supply it.
 */
export const updatePasswordInputAuthenticatedSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: passwordSchema,
});
export const updatePasswordInputUnauthenticatedSchema = z.object({
  newPassword: passwordSchema,
  userId: z.string(),
  token: z.string(),
});
export const updatePasswordRequestSchema = {
  // Order matters: zod unions take the FIRST matching member and strip
  // unknown keys, so the more specific reset-token shape comes first. The two
  // are disjoint now that the authenticated shape requires currentPassword —
  // this used to be load-bearing, when {newPassword} matched any body and
  // silently dropped userId/token. Keep the order regardless.
  body: z.union([
    updatePasswordInputUnauthenticatedSchema,
    updatePasswordInputAuthenticatedSchema,
  ]),
  response: { 200: z.any() },
};
export type UpdatePasswordBody = z.infer<
  typeof updatePasswordRequestSchema.body
>;

/**
 * Asks whether an emailed activation / reset link is still usable, WITHOUT
 * spending it. The page behind both links has to know before it renders: a
 * spent link used to open a live-looking password form and only refuse after
 * the person had chosen and typed a new password twice.
 *
 * Same shape as the reset branch of `PUT /users/password` minus the password,
 * and it answers the SAME error codes that branch would — so a client has one
 * set of messages for "this link is dead", whichever call surfaced it. A 200
 * therefore means "submitting this token right now would be accepted"; it is
 * not a promise, since the link can be superseded a moment later.
 *
 * POST rather than GET on purpose: the token stays out of query strings, which
 * is what proxy and access logs record. It leaks nothing the consuming route
 * does not already leak to anyone willing to submit a password with it.
 */
export const verifyPasswordTokenRequestSchema = {
  body: z.object({ userId: z.string(), token: z.string() }),
  response: { 200: z.object({ valid: z.literal(true) }) },
};
export type VerifyPasswordTokenBody = z.infer<
  typeof verifyPasswordTokenRequestSchema.body
>;

export const getUserByIdRequestSchema = {
  params: z.object({ id: z.string() }),
  response: { 200: userResponseSchema },
};
export type GetUserByIdParams = z.infer<typeof getUserByIdRequestSchema.params>;

export const updateUserRequestSchema = {
  params: z.object({ id: z.string() }),
  body: userResponseSchema
    .pick({
      firstName: true,
      lastName: true,
      email: true,
      gender: true,
      phone: true,
      birthdate: true,
      // Promoting or demoting an admin rides on the same PATCH as every other
      // account edit. Gated: the route's self-rule sends anything beyond a
      // user's own preferredLanguage through MANAGE_USERS, so nobody can flip
      // this on themselves.
      isAdmin: true,
      // Archiving was one-way with no path back; this is the un-archive.
      isArchived: true,
      // How the account page saves the signed-in clinician's own UI language.
      // It rides on the existing user PATCH rather than getting a route of its
      // own: it is a column on this row like every other field here, and the
      // account page already writes to its own user id.
      preferredLanguage: true,
      // A clinician changes function, moves department, or was created before
      // their registration number was to hand. All of it is editable, and none
      // of it is authorization.
      professionType: true,
      ...optionalProfessionalMask,
    })
    .partial(),
  response: { 200: userResponseSchema },
};
export type UpdateUserParams = z.infer<typeof updateUserRequestSchema.params>;
export type UpdateUserBody = z.infer<typeof updateUserRequestSchema.body>;

export const archiveUserRequestSchema = {
  params: z.object({ id: z.string() }),
  response: { 200: userResponseSchema },
};
export type ArchiveUserBody = z.infer<typeof archiveUserRequestSchema.params>;

/**
 * Re-send the activation email for an account that never activated. Fails with
 * USER_ALREADY_ACTIVATED on an activated account and ACCOUNT_ARCHIVED on an
 * archived one — there is nothing to activate in either case.
 */
export const resendActivationRequestSchema = {
  params: z.object({ id: z.string() }),
  response: { 200: userResponseSchema },
};
export type ResendActivationParams = z.infer<
  typeof resendActivationRequestSchema.params
>;
